import React, { useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";

// Canvas de firma — implementación mínima sin libs externas. Captura
// mouse + touch, traza con `quadraticCurveTo` para suavizar y devuelve
// un dataURL PNG con `toDataURL` vía `onChange`.
//
// Reusado por:
//   - `EmployeeClockingEdit` (admin firma inmediata).
//   - `PendingSignaturesModal` (kiosk: empleado firma sus pendientes).
//   - `/sign-clocking/:token` (página pública desde el email).
//
// Si necesitas trazos más complejos (varias capas, reset parcial, etc.),
// pasar a una lib tipo `react-signature-canvas` — esto es deliberadamente
// minimalista para no añadir dependencia.
export default function SignaturePad({ onChange, disabled, placeholder = "Firma del trabajador" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  // Reescala el canvas para que el ancho real (devicePixelRatio) coincida
  // con el ancho CSS — sin esto las firmas salen pixeladas en pantallas retina.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1f2937";
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: point.clientX - rect.left,
      y: point.clientY - rect.top,
    };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    lastPoint.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPoint(e);
    const last = lastPoint.current;
    const mid = { x: (last.x + p.x) / 2, y: (last.y + p.y) / 2 };
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.quadraticCurveTo(last.x, last.y, mid.x, mid.y);
    ctx.stroke();
    lastPoint.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange?.("");
  };

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          border: "1.5px dashed",
          borderColor: hasInk ? "primary.main" : "grey.400",
          borderRadius: 2,
          height: 160,
          backgroundColor: "#fafafa",
          overflow: "hidden",
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          sx={{
            width: "100%",
            height: "100%",
            touchAction: "none",
            cursor: disabled ? "not-allowed" : "crosshair",
          }}
        />
        {!hasInk && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: "text.disabled",
              fontStyle: "italic",
            }}
          >
            {placeholder}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <Button size="small" onClick={clear} color="inherit" disabled={!hasInk}>
          Borrar firma
        </Button>
      </Box>
    </Box>
  );
}
